import React, { Component, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import { ValidationError, ExpandingTextarea } from '../../components';

import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

/**
 * The two states of the password show/hide toggle: an open eye ("show") and a
 * crossed-out eye ("hide"). These are inlined instead of being added as an
 * IconEye component, because the password toggle is the only thing that uses
 * them.
 */
const EyeIcon = props => {
  const { crossedOut } = props;
  return (
    <svg
      className={css.eyeIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {crossedOut ? (
        <>
          <path d="M17.94 17.94A10.1 10.1 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <path d="M1 1l22 22" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
};

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    ...rest
  } = props;

  const intl = useIntl();
  // Only used by password fields. The value lives in final-form as usual, so
  // flipping this changes nothing but the input's type attribute.
  const [showPassword, setShowPassword] = useState(false);

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const isPassword = input.type === 'password';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  // A revealed password field renders as a plain text input.
  const inputType = isPassword && showPassword ? 'text' : type;

  const inputClasses = classNames(
    inputRootClass ||
      classNames(css.input, {
        [css.inputSuccess]: valid,
        [css.inputError]: hasError,
        [css.textarea]: isTextarea,
      }),
    // Added even when inputRootClass is given, so typed text never runs under
    // the toggle button.
    { [css.passwordInput]: isPassword }
  );
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
        // Last, because `input` carries its own `type` and would win otherwise.
        type: inputType,
      }
    : { className: inputClasses, id, ...refMaybe, ...input, ...rest, type: inputType };

  const classes = classNames(rootClassName || css.root, className);
  const inputElement = isTextarea ? (
    <ExpandingTextarea {...inputProps} />
  ) : (
    <input {...inputProps} />
  );

  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      {isPassword ? (
        <div className={css.passwordWrapper}>
          {inputElement}
          <button
            type="button"
            className={css.passwordToggle}
            aria-label={intl.formatMessage({
              id: showPassword ? 'FieldTextInput.hidePassword' : 'FieldTextInput.showPassword',
            })}
            // Taking focus on mousedown would pull the caret out of the field,
            // so it is prevented. Keyboard users still tab to the button and
            // activate it with Enter or Space.
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              // type="button" already keeps this out of the form's submit, but
              // being explicit costs nothing.
              e.preventDefault();
              setShowPassword(prevShowPassword => !prevShowPassword);
            }}
          >
            <EyeIcon crossedOut={showPassword} />
          </button>
        </div>
      ) : (
        inputElement
      )}
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
